import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// ONE app-wide source for the inbox unread count. Every badge (desktop header, admin sidebar,
// mobile menu, mobile hamburger) reads from here so they always agree and update together.
//
// RELIABILITY (why this exists): the old per-component setInterval polls kept running in the
// background, but mobile browsers suspend JS on a backgrounded tab and never fired a fresh poll
// on return — so a member who switched apps and came back saw a stale/zero badge until a later
// tick. Here we poll ONLY while the tab is visible AND refetch IMMEDIATELY on foreground/focus,
// so returning to the app surfaces new messages without opening the inbox.
const InboxUnreadContext = createContext({ total: 0, sections: null, refresh: () => {} });

export function useInboxUnread() {
  return useContext(InboxUnreadContext);
}

export function InboxUnreadProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ total: 0, sections: null });
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) { setState({ total: 0, sections: null }); return; }
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await api.get("/inbox/summary");
      setState({ total: data.total || 0, sections: data.sections || null });
    } catch { /* keep the previous count on a transient error */ }
    finally { inFlight.current = false; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  useEffect(() => {
    if (!user) { setState({ total: 0, sections: null }); return; }
    refresh();
    const tick = () => { if (document.visibilityState === "visible") refresh(); };
    const id = setInterval(tick, 8000);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, refresh]);

  return (
    <InboxUnreadContext.Provider value={{ total: state.total, sections: state.sections, refresh }}>
      {children}
    </InboxUnreadContext.Provider>
  );
}
